import {Siren} from "lucide-react";

import {cn} from "@/lib/utils";

import {
  DISPATCH_DESK_CONTENT_MUTED_CLASS,
  DISPATCH_DESK_NESTED_INDENT_CLASS,
  DispatchDeskCardShell,
  DispatchDeskContactRow,
  DispatchDeskEmptyMessage,
  DispatchDeskEmptyPanelBody,
  DispatchDeskNestedList,
  DispatchDeskScrollRegion,
} from "./dispatch-desk-card";
import {useDispatchDeskProperty} from "../hooks/use-dispatch-desk-property";
import {type ContactGroup, groupContactsByType} from "../lib/contact-groups";
import {
  DISPATCH_DESK_CAPPED_CARD_CONTENT_CLASS,
  DISPATCH_DESK_MOBILE_TALL_PANEL_CAP_CLASS,
  DISPATCH_DESK_NO_PROPERTY_MESSAGE,
  DISPATCH_DESK_PANEL_CLASS,
} from "../lib/dispatch-desk-layout";

type DispatchPropertyEmergencyContactsProps = {
  className?: string;
};

function EmergencyContactGroup({group}: {group: ContactGroup}) {
  return (
    <div className={cn("flex flex-col gap-1.5", DISPATCH_DESK_NESTED_INDENT_CLASS)}>
      <div className="flex items-center gap-1.5">
        <group.icon
          className={cn(
            "size-3 shrink-0",
            group.isLifeSafety ? "text-destructive" : "text-muted-foreground",
          )}
          aria-hidden
        />
        <span
          className={cn(
            DISPATCH_DESK_CONTENT_MUTED_CLASS,
            "font-normal",
            group.isLifeSafety ? "text-destructive" : undefined,
          )}
        >
          {group.label}
        </span>
      </div>
      <DispatchDeskNestedList>
        {group.contacts.map((contact) => (
          <DispatchDeskContactRow
            key={contact.contact_telephone_id}
            name={contact.name}
            phone={contact.telephone}
            callLabel={contact.name}
          />
        ))}
      </DispatchDeskNestedList>
    </div>
  );
}

export function DispatchPropertyEmergencyContacts({
  className,
}: DispatchPropertyEmergencyContactsProps) {
  const property = useDispatchDeskProperty();
  const contacts = property?.contact_telephones ?? [];
  const groups = groupContactsByType(contacts);

  return (
    <DispatchDeskCardShell
      className={cn(
        DISPATCH_DESK_PANEL_CLASS,
        DISPATCH_DESK_MOBILE_TALL_PANEL_CAP_CLASS,
        className,
      )}
      contentClassName={DISPATCH_DESK_CAPPED_CARD_CONTENT_CLASS}
      icon={Siren}
      iconClassName="text-destructive"
      title="Emergency contacts"
    >
      <DispatchDeskScrollRegion capped>
        {!property ? (
          <DispatchDeskEmptyPanelBody>
            <DispatchDeskEmptyMessage>{DISPATCH_DESK_NO_PROPERTY_MESSAGE}</DispatchDeskEmptyMessage>
          </DispatchDeskEmptyPanelBody>
        ) : groups.length === 0 ? (
          <DispatchDeskEmptyPanelBody>
            <DispatchDeskEmptyMessage>No emergency contacts on file.</DispatchDeskEmptyMessage>
          </DispatchDeskEmptyPanelBody>
        ) : (
          <div className="flex flex-col gap-3">
            {groups.map((group, index) => (
              <div key={group.type} className={cn(index > 0 && "border-border border-t pt-3")}>
                <EmergencyContactGroup group={group} />
              </div>
            ))}
          </div>
        )}
      </DispatchDeskScrollRegion>
    </DispatchDeskCardShell>
  );
}
